import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Divider,
  InputNumber,
  Row,
  Select,
  Slider,
  Space,
  Statistic,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { CalculatorOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { adminApi } from '@/services/api';
import type { FrCalcInput, FrCalcResult } from '@/types';

const { Title, Text } = Typography;

interface CurveCfg {
  maxStatFR: number;
  curveStrength: number;
  baseWinFactor: number;
}

interface FrCalculatorProps {
  // Current saved curve config — seeds the playground when opened / reset.
  savedConfig: CurveCfg;
  // Push the playground values up into the Save form above.
  onApplyConfig?: (cfg: CurveCfg) => void;
}

// Numeric keys of the match scenario (won/draw/matchType handled separately).
type NumKey = Exclude<keyof FrCalcInput, 'won' | 'draw' | 'matchType'>;

// Match scenario prefilled with the GDD worked example (→ 25 FR at default config).
const DEFAULT_SCENARIO: FrCalcInput = {
  won: true,
  draw: false,
  matchType: 'custom_match',
  playerSingles: 1,
  playerDoubles: 4,
  playerTriples: 3,
  playerFours: 4,
  playerSixes: 4,
  playerExtras: 0,
  opponentSingles: 1,
  opponentDoubles: 4,
  opponentTriples: 3,
  opponentFours: 5,
  opponentSixes: 2,
  opponentExtras: 1,
  playerBallsSaved: 0,
  opponentBallsSaved: 0,
  playerWicketsTaken: 1,
  playerWicketsSaved: 9,
  opponentWicketsTaken: 2,
  opponentWicketsSaved: 10,
  runDifference: 7,
  playerTeamRating: 85,
  opponentTeamRating: 95,
  teamFactor: 2, // My Squad (neutral). National teams differ — see the team preset.
  matchFactor: 0, // Blitz-only; 0 for Custom/Quick Play/Tournament (matches the server).
};

// Per-team factors — mirrors the backend TEAMS config
// (wcc4-backend/src/modules/game-config/game-config.data.ts). Drives the Team
// Factor field so the simulator uses the right team's factor instead of always
// defaulting Custom Matches to the neutral Player Squad value. Keep in sync with
// the backend list. Player Squad = 2 (neutral under WinBonus' TF/2).
const TEAM_FACTOR_PRESETS: { value: string; label: string; tf: number }[] = [
  { value: 'player_squad', label: 'My Squad / Player Squad (2)', tf: 2 },
  { value: 'india', label: 'India (1.5)', tf: 1.5 },
  { value: 'australia', label: 'Australia (1.4)', tf: 1.4 },
  { value: 'england', label: 'England (1.3)', tf: 1.3 },
  { value: 'pakistan', label: 'Pakistan (1.2)', tf: 1.2 },
  { value: 'south_africa', label: 'South Africa (1.2)', tf: 1.2 },
  { value: 'new_zealand', label: 'New Zealand (1.1)', tf: 1.1 },
  { value: 'west_indies', label: 'West Indies (1.0)', tf: 1.0 },
  { value: 'sri_lanka', label: 'Sri Lanka (1.0)', tf: 1.0 },
  { value: 'bangladesh', label: 'Bangladesh (0.9)', tf: 0.9 },
  { value: 'afghanistan', label: 'Afghanistan (0.85)', tf: 0.85 },
  { value: 'ireland', label: 'Ireland (0.8)', tf: 0.8 },
  { value: 'zimbabwe', label: 'Zimbabwe (0.75)', tf: 0.75 },
  { value: 'custom', label: 'Custom…', tf: NaN },
];

/**
 * FR playground. Hidden until "Show FR Calculator" is clicked. Lets an admin
 * tweak the three curve config values (maxStatFR / curveStrength / baseWinFactor)
 * against a fixed match scenario and watch the FR recompute live — WITHOUT
 * saving. The result is computed on the server (the real FR calculation), with
 * the playground values passed as per-call overrides. "Apply to config form"
 * copies the values up into the Save form.
 */
export const FrCalculator: React.FC<FrCalculatorProps> = ({ savedConfig, onApplyConfig }) => {
  const [visible, setVisible] = useState(false);
  const [cfg, setCfg] = useState<CurveCfg>(savedConfig);
  const [scenario, setScenario] = useState<FrCalcInput>(DEFAULT_SCENARIO);
  const [result, setResult] = useState<FrCalcResult | null>(null);
  const [loading, setLoading] = useState(false);
  // Auto-recompute only kicks in after the first explicit "Calculate".
  const startedRef = useRef(false);
  // Selected Team Factor preset. Tracked separately from teamFactor because some
  // teams share a factor (Pakistan & South Africa = 1.2, West Indies & Sri Lanka
  // = 1.0), so the dropdown selection can't be derived from the value alone.
  const [teamPreset, setTeamPreset] = useState<string>('player_squad');

  const calculate = useCallback(async (c: CurveCfg, s: FrCalcInput) => {
    setLoading(true);
    try {
      const res = await adminApi.calculateFr({
        ...s,
        ...c,
      } as unknown as Record<string, number | boolean | string>);
      setResult(res.data);
    } catch (err: unknown) {
      const apiErr = err as {
        response?: { data?: { message?: string | string[] } };
      };
      const apiMsg = Array.isArray(apiErr?.response?.data?.message)
        ? apiErr.response.data.message.join(', ')
        : apiErr?.response?.data?.message;
      message.error(apiMsg || 'Failed to calculate FR');
    } finally {
      setLoading(false);
    }
  }, []);

  // Live recompute (debounced) whenever the config or scenario changes, once
  // the first calculation has been run.
  useEffect(() => {
    if (!startedRef.current) return;
    const t = setTimeout(() => calculate(cfg, scenario), 250);
    return () => clearTimeout(t);
  }, [cfg, scenario, calculate]);

  const open = () =>
    setVisible((v) => {
      const next = !v;
      if (next) setCfg(savedConfig); // seed playground from the saved config
      return next;
    });

  const runFirst = () => {
    startedRef.current = true;
    calculate(cfg, scenario);
  };

  const setNum = (key: NumKey) => (v: number | null) =>
    setScenario((p) => ({ ...p, [key]: typeof v === 'number' ? v : 0 }));

  const setCfgVal = (key: keyof CurveCfg) => (v: number | null) =>
    setCfg((c) => ({ ...c, [key]: typeof v === 'number' ? v : c[key] }));

  const dirty =
    cfg.maxStatFR !== savedConfig.maxStatFR ||
    cfg.curveStrength !== savedConfig.curveStrength ||
    cfg.baseWinFactor !== savedConfig.baseWinFactor;

  const numField = (label: string, key: NumKey, min = 0, max?: number) => (
    <Col xs={12} sm={8} md={6} key={key}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Text>
      <InputNumber
        min={min}
        max={max}
        value={scenario[key]}
        onChange={setNum(key)}
        style={{ width: '100%' }}
      />
    </Col>
  );

  const cfgRow = (label: string, key: keyof CurveCfg, min: number, max: number, hint: string) => (
    <div>
      <Space size={4}>
        <Text strong>{label}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {hint}
        </Text>
      </Space>
      <Row align="middle" gutter={12}>
        <Col flex="auto">
          <Slider min={min} max={max} value={cfg[key]} onChange={setCfgVal(key)} />
        </Col>
        <Col>
          <InputNumber min={min} value={cfg[key]} onChange={setCfgVal(key)} style={{ width: 90 }} />
        </Col>
      </Row>
    </div>
  );

  return (
    <div>
      <Divider style={{ margin: '8px 0' }} />
      <Button icon={<CalculatorOutlined />} onClick={open}>
        {visible ? 'Hide FR Calculator' : 'Show FR Calculator'}
      </Button>

      {visible && (
        <Card size="small" style={{ marginTop: 16 }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Title level={5} style={{ marginBottom: 0 }}>
                FR Curve Playground
              </Title>
              <Text type="secondary">
                Drag the three curve values and watch the FR for the scenario below recompute live
                (server-side, mirrors a real match). Nothing is saved until you click{' '}
                <b>Apply to config form</b> and then <b>Save</b>.
              </Text>
            </div>

            {/* The three configurable curve values — the playground */}
            <Card
              size="small"
              type="inner"
              title="Curve config (not saved)"
              extra={dirty ? <Tag color="orange">modified</Tag> : <Tag>matches saved</Tag>}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {cfgRow('Max Stat FR', 'maxStatFR', 0, 300, 'curve height (def 100)')}
                {cfgRow('Curve Strength', 'curveStrength', 1, 300, 'higher = gentler (def 75)')}
                {cfgRow('Base Win Factor', 'baseWinFactor', 0, 100, 'win bonus base (def 20)')}
                <Space wrap>
                  <Button size="small" onClick={() => setCfg(savedConfig)} disabled={!dirty}>
                    Reset to saved
                  </Button>
                  {onApplyConfig && (
                    <Button
                      size="small"
                      type="dashed"
                      icon={<ArrowUpOutlined />}
                      onClick={() => onApplyConfig(cfg)}
                      disabled={!dirty}
                    >
                      Apply to config form
                    </Button>
                  )}
                </Space>
              </Space>
            </Card>

            {/* The fixed match scenario the curve is evaluated against */}
            <div>
              <Text strong>Match scenario</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Match Factor applies to Blitz Showdown only; Team Factor affects wins only and is
                per-team (My Squad = 2, national teams vary).
              </Text>
            </div>
            <Row gutter={[12, 12]} align="bottom">
              <Col xs={12} sm={8} md={6}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Match Type
                </Text>
                <Select
                  value={scenario.matchType}
                  style={{ width: '100%' }}
                  onChange={(v) =>
                    setScenario((p) => ({
                      ...p,
                      matchType: v,
                      // Match Factor is a Blitz-only bonus; the server sends 0 for
                      // every other mode, so keep the simulator in sync.
                      matchFactor: v === 'blitz_showdown' ? p.matchFactor || 1 : 0,
                    }))
                  }
                  options={[
                    { value: 'quick_play', label: 'Quick Play' },
                    { value: 'custom_match', label: 'Custom Match' },
                    { value: 'blitz_showdown', label: 'Blitz Showdown' },
                    { value: 'tournament', label: 'Tournament' },
                  ]}
                />
              </Col>
              <Col xs={12} sm={8} md={6}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Won
                  </Text>
                </div>
                <Switch
                  checked={scenario.won}
                  onChange={(c) => setScenario((p) => ({ ...p, won: c }))}
                />
              </Col>
              <Col xs={12} sm={8} md={6}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Draw (tie)
                  </Text>
                </div>
                <Switch
                  checked={scenario.draw}
                  onChange={(c) => setScenario((p) => ({ ...p, draw: c }))}
                />
              </Col>
              {scenario.matchType === 'blitz_showdown' &&
                numField('Match Factor (Blitz round)', 'matchFactor')}
            </Row>

            <Row gutter={[12, 12]}>
              {numField('Player Rating (PTR)', 'playerTeamRating', 1)}
              {numField('Opponent Rating (OTR)', 'opponentTeamRating', 1)}
              <Col xs={12} sm={8} md={6}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Team Factor (per-team; win only)
                </Text>
                <Select
                  showSearch
                  optionFilterProp="label"
                  value={teamPreset}
                  style={{ width: '100%' }}
                  onChange={(v) => {
                    setTeamPreset(v);
                    const preset = TEAM_FACTOR_PRESETS.find((t) => t.value === v);
                    if (preset && !Number.isNaN(preset.tf)) {
                      setScenario((p) => ({ ...p, teamFactor: preset.tf }));
                    }
                  }}
                  options={TEAM_FACTOR_PRESETS.map((t) => ({
                    value: t.value,
                    label: t.label,
                  }))}
                />
                <InputNumber
                  min={0}
                  max={2}
                  step={0.05}
                  value={scenario.teamFactor}
                  onChange={(v) => {
                    // Manual edit → the value may not match the picked team.
                    setTeamPreset('custom');
                    setScenario((p) => ({
                      ...p,
                      teamFactor: typeof v === 'number' ? v : 0,
                    }));
                  }}
                  style={{ width: '100%', marginTop: 4 }}
                />
                <Text
                  italic
                  type="secondary"
                  style={{ fontSize: 11, display: 'block', marginTop: 4 }}
                >
                  Presets are the backend game-config (code-level) values. To use any other value
                  (e.g. as used in Unity), pick the Custom option.
                </Text>
              </Col>
              {numField('Run Difference', 'runDifference')}
            </Row>

            <Text strong>Player batting</Text>
            <Row gutter={[12, 12]}>
              {numField('Singles', 'playerSingles')}
              {numField('Doubles', 'playerDoubles')}
              {numField('Triples', 'playerTriples')}
              {numField('Fours', 'playerFours')}
              {numField('Sixes', 'playerSixes')}
              {numField('Extras', 'playerExtras')}
            </Row>

            <Text strong>Opponent batting</Text>
            <Row gutter={[12, 12]}>
              {numField('Singles', 'opponentSingles')}
              {numField('Doubles', 'opponentDoubles')}
              {numField('Triples', 'opponentTriples')}
              {numField('Fours', 'opponentFours')}
              {numField('Sixes', 'opponentSixes')}
              {numField('Extras', 'opponentExtras')}
            </Row>

            <Text strong>Wickets &amp; balls</Text>
            <Row gutter={[12, 12]}>
              {numField('Player Wkts Taken', 'playerWicketsTaken')}
              {numField('Player Wkts Saved', 'playerWicketsSaved')}
              {numField('Opp Wkts Taken', 'opponentWicketsTaken')}
              {numField('Opp Wkts Saved', 'opponentWicketsSaved')}
              {numField('Player Balls Saved', 'playerBallsSaved')}
              {numField('Opp Balls Saved', 'opponentBallsSaved')}
            </Row>

            <Space wrap>
              <Button
                type="primary"
                icon={<CalculatorOutlined />}
                onClick={runFirst}
                loading={loading}
              >
                Calculate FR
              </Button>
              <Button
                onClick={() => {
                  setScenario(DEFAULT_SCENARIO);
                  setTeamPreset('player_squad');
                }}
              >
                Reset scenario
              </Button>
              {startedRef.current && (
                <Text type="secondary">Live — updates as you change values</Text>
              )}
            </Space>

            {result && (
              <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                <Space size="large" align="center" wrap>
                  <Statistic
                    title="FR Change"
                    value={result.fanRatingChange}
                    valueStyle={{
                      color: result.fanRatingChange >= 0 ? '#3f8600' : '#cf1322',
                    }}
                    prefix={result.fanRatingChange >= 0 ? '+' : ''}
                  />
                  <div>
                    <Text type="secondary">Config used</Text>
                    <br />
                    <Tag>maxStatFR {result.config.maxStatFR}</Tag>
                    <Tag>curveStrength {result.config.curveStrength}</Tag>
                    <Tag>baseWinFactor {result.config.baseWinFactor}</Tag>
                  </div>
                </Space>
              </Card>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default FrCalculator;
